import * as THREE from 'three';
import type { BoardNode } from '../../../shared/board/types';
import type { MapHandle } from '../maps';
import type { GsapRegistry } from '../vfx/gsap-registry';
import { getWorldPositionForNode, type SurfaceLookup } from './surface-y';

export type BoardOverlayAssetKind = 'loading' | 'glb' | 'procedural';

export interface BoardObjectGltfLoader {
  load(url: string): Promise<THREE.Group>;
}

export interface BoardObjectOverlay {
  readonly group: THREE.Group;
  readonly assetKind: BoardOverlayAssetKind;
  update(activeNodeId: string | null, mapHandle: MapHandle): void;
  playFirstAppearance(activeNodeId: string | null, mapHandle: MapHandle): Promise<void>;
  playRelocationAppearance(activeNodeId: string | null, mapHandle: MapHandle): Promise<void>;
  dispose(): void;
}

const CHEST_MODEL_URL = '/models/treasure-chest.glb';
const SURFACE_OFFSET = 0.04;
const TARGET_MAX_DIMENSION = 0.72;
const RISE_DEPTH = 2;
const RISE_DURATION_SECONDS = 0.6;

function findNode(nodeId: string, mapHandle: MapHandle): BoardNode | undefined {
  return (mapHandle.schema.nodes as readonly BoardNode[]).find((node) => node.id === nodeId);
}

function disposeObject(root: THREE.Object3D): void {
  root.traverse((obj) => {
    if (!(obj instanceof THREE.Mesh)) return;
    obj.geometry?.dispose();
    const material = obj.material;
    if (Array.isArray(material)) {
      for (const mat of material) mat.dispose();
    } else {
      material.dispose();
    }
  });
}

function normalizeToTileFootprint(root: THREE.Object3D): void {
  root.updateWorldMatrix(true, true);
  const box = new THREE.Box3().setFromObject(root);
  const size = box.getSize(new THREE.Vector3());
  const maxDimension = Math.max(size.x, size.y, size.z);
  if (maxDimension > 0) {
    root.scale.multiplyScalar(TARGET_MAX_DIMENSION / maxDimension);
    root.updateWorldMatrix(true, true);
    box.setFromObject(root);
  }

  const center = box.getCenter(new THREE.Vector3());
  root.position.x -= center.x;
  root.position.y -= box.min.y;
  root.position.z -= center.z;
}

function buildProceduralChest(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'treasure-chest-procedural';

  const bodyMat = new THREE.MeshStandardMaterial({
    color: 0x7a3f19,
    roughness: 0.65,
  });
  const trimMat = new THREE.MeshStandardMaterial({
    color: 0xf2c84b,
    emissive: 0xf2c84b,
    emissiveIntensity: 0.28,
    metalness: 0.45,
    roughness: 0.25,
  });

  const body = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.32, 0.35), bodyMat);
  body.position.y = 0.16;
  group.add(body);

  const lid = new THREE.Mesh(new THREE.BoxGeometry(0.56, 0.12, 0.4), trimMat);
  lid.position.y = 0.38;
  group.add(lid);

  const lock = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.12, 0.04), trimMat);
  lock.position.set(0, 0.23, 0.2);
  group.add(lock);

  return group;
}

export function createChestOverlay(args: {
  parent: THREE.Group;
  surfaceLookup: SurfaceLookup;
  gltfLoader?: BoardObjectGltfLoader | null;
  gsapRegistry?: GsapRegistry | null;
}): BoardObjectOverlay {
  const group = new THREE.Group();
  group.name = 'treasure-chest-overlay';
  group.visible = false;
  args.parent.add(group);

  let disposed = false;
  let currentNodeId: string | null = null;
  let currentMapHandle: MapHandle | null = null;
  let visual: THREE.Object3D | null = buildProceduralChest();
  let assetKind: BoardOverlayAssetKind = args.gltfLoader == null ? 'procedural' : 'loading';
  let firstAppearanceStarted = false;
  let riseHandle: ReturnType<GsapRegistry['create']> | null = null;
  group.add(visual);

  function resolveCurrentPlacement(): { position: THREE.Vector3; finalY: number } | null {
    if (currentNodeId == null || currentMapHandle == null) {
      group.visible = false;
      return null;
    }

    const node = findNode(currentNodeId, currentMapHandle);
    if (node == null) {
      group.visible = false;
      return null;
    }

    const position = getWorldPositionForNode(node, args.surfaceLookup);
    return { position, finalY: position.y + SURFACE_OFFSET };
  }

  function placeCurrent(): void {
    const placement = resolveCurrentPlacement();
    if (placement == null) return;

    const { position, finalY } = placement;
    group.position.set(position.x, finalY, position.z);
    group.visible = true;
  }

  function replaceVisual(nextVisual: THREE.Object3D, nextKind: BoardOverlayAssetKind): void {
    if (visual != null) {
      group.remove(visual);
      disposeObject(visual);
    }
    visual = nextVisual;
    assetKind = nextKind;
    normalizeToTileFootprint(visual);
    group.add(visual);
    if (riseHandle == null) {
      placeCurrent();
    }
  }

  function stopRise(): void {
    if (riseHandle == null) return;
    riseHandle.dispose();
    riseHandle = null;
  }

  function playRiseFromUnderground(force = false): Promise<void> {
    const placement = resolveCurrentPlacement();
    if (placement == null) return Promise.resolve();
    if ((!force && firstAppearanceStarted) || args.gsapRegistry == null) {
      placeCurrent();
      return Promise.resolve();
    }

    firstAppearanceStarted = true;
    stopRise();

    const { position, finalY } = placement;
    group.position.set(position.x, finalY - RISE_DEPTH, position.z);
    group.visible = true;

    return new Promise((resolve) => {
      const handle = args.gsapRegistry!.create('treasure-chest:first-rise');
      riseHandle = handle;
      handle.timeline.to(
        group.position,
        {
          y: finalY,
          duration: RISE_DURATION_SECONDS,
          ease: 'power3.out',
        },
        0,
      );
      handle.timeline.call(
        () => {
          if (riseHandle === handle) {
            riseHandle = null;
          }
          handle.dispose();
          group.position.set(position.x, finalY, position.z);
          resolve();
        },
        undefined,
        RISE_DURATION_SECONDS,
      );
    });
  }

  if (args.gltfLoader != null) {
    void args.gltfLoader
      .load(CHEST_MODEL_URL)
      .then((scene) => {
        if (disposed) {
          disposeObject(scene);
          return;
        }
        scene.name = 'treasure-chest-glb';
        replaceVisual(scene, 'glb');
      })
      .catch((err) => {
        console.warn('[smashparty] treasure chest GLB failed; using procedural fallback:', err);
        assetKind = 'procedural';
        placeCurrent();
      });
  }

  return {
    group,
    get assetKind(): BoardOverlayAssetKind {
      return assetKind;
    },
    update(activeNodeId: string | null, mapHandle: MapHandle): void {
      currentMapHandle = mapHandle;
      if (activeNodeId === currentNodeId) return;
      stopRise();
      currentNodeId = activeNodeId;
      placeCurrent();
    },
    playFirstAppearance(activeNodeId: string | null, mapHandle: MapHandle): Promise<void> {
      currentMapHandle = mapHandle;
      if (activeNodeId == null || activeNodeId === '') {
        stopRise();
        currentNodeId = activeNodeId;
        placeCurrent();
        return Promise.resolve();
      }
      const wasHidden = currentNodeId == null || currentNodeId === '';
      currentNodeId = activeNodeId;
      if (!wasHidden) {
        placeCurrent();
        return Promise.resolve();
      }
      return playRiseFromUnderground();
    },
    playRelocationAppearance(activeNodeId: string | null, mapHandle: MapHandle): Promise<void> {
      currentMapHandle = mapHandle;
      if (activeNodeId == null || activeNodeId === '') {
        stopRise();
        currentNodeId = activeNodeId;
        placeCurrent();
        return Promise.resolve();
      }
      currentNodeId = activeNodeId;
      return playRiseFromUnderground(true);
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      stopRise();
      args.parent.remove(group);
      if (visual != null) {
        disposeObject(visual);
        visual = null;
      }
      group.clear();
    },
  };
}
